import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  type FeedbackKind,
  type FeedbackPage,
  type OpenFeedback,
  type VoiceEndReason,
} from '@myrewards/contracts';
import { FEEDBACK_VOICE_LIMITS } from '@myrewards/domain';
import { ApiError, isNotSignedIn } from '../api/errors';
import { useToast } from '../ui/Toast';
import {
  completeScreenshot,
  discardFeedback,
  openFeedback,
  submitFeedback,
  uploadScreenshot,
  voiceUsedUp,
} from './api';
import { captureScreen, type CapturedScreen } from './capture';
import { clientState } from './clientState';
import { dictationPlatform } from './dictation';
import { recordable, snapshot } from './diagnostics';
import { FeedbackReview, WORDS_REQUIRED, type ShotView } from './FeedbackReview';
import {
  explainMicrophone,
  isMicrophoneError,
  watchMicrophone,
  type MicrophoneHelp,
} from './voice/micAccess';
import { openMicrophone } from './voice/microphone';
import {
  startRecorder,
  voiceUrl,
  wordsOf,
  type Recorder,
  type RecorderPhase,
  type RecorderResult,
  type VoiceTicket,
} from './voice/recorder';
import { RecordingBar } from './voice/RecordingBar';

// APP-104 §3.2–3.5, §5.3. One flow, mounted once in the app shell: the header button, the account
// menu and the Help panel all open it. The server's own error text never reaches the screen;
// every message below is fixed. With the app's own voice offered, the click shows the recording
// bar first and the review follows it; otherwise the review opens at once, for typing. The first
// time, the shell shows the voice introduction before this mounts (§3.2a).

/** The upload gets this long (ms) before the report is told it isn't coming. */
export const UPLOAD_TIMEOUT_MS = 240_000;

export interface FeedbackFeature {
  /** The app's own voice is offered (`/v1/me`); the dictation tip then stays out. */
  voice: boolean;
  /** The System Admin setting that shows the tip. */
  dictationTip: boolean;
  /** Voice time left today, in whole seconds (0: used up); absent from an older server. */
  voiceSecondsLeft?: number | undefined;
  /**
   * The person hasn't been through the voice introduction in this browser (§3.2a): the shell
   * shows it before the first recording. The flow itself doesn't read it.
   */
  voiceIntro?: boolean | undefined;
}

/** Mounted only while the dialog is open: each opening is a fresh mount (and a fresh draft). */
export interface FeedbackFlowProps {
  features: FeedbackFeature;
  /** Where the person is, in the shell's words (the Level 1 item). */
  area: string;
  hero: 'full' | 'slim';
  /** Type instead, chosen in the voice introduction: the review first, with Record still offered. */
  startTyping?: boolean;
  /** The flow ended: sent, discarded or closed. */
  onClose: () => void;
  /** The server says feedback is off: the shell hides its entries. */
  onOff: () => void;
  /** The session ended (401): the app takes the person to sign-in. */
  onSessionEnded: () => void;
  /** A recording ended: the day's voice time has changed. */
  onVoiceUsed?: () => void;
  /** It was sent: the person's own list of feedback has a new one (APP-138). */
  onSent?: () => void;
}

const LIMIT_WORDS = 'You’ve sent a lot of feedback today. Please try again tomorrow.';
const OPEN_LIMIT_WORDS =
  'You’ve started a lot of feedback in the last hour. Please try again a little later.';
const TRY_AGAIN = 'We couldn’t send your feedback. Check your connection and try again.';
/** APP-138: the thanks says where to follow it. */
const FOLLOW_IT = 'You can follow it, and read our replies, in Settings › My feedback.';
const NO_SHOT: Record<string, string> = {
  too_large: 'it was too large to attach.',
  unreadable: 'it couldn’t be read.',
  too_many_pixels: 'it was larger than the supported image size.',
  failed: 'it couldn’t be taken.',
};

const NO_UPLOAD_TOAST = {
  tone: 'info',
  title: 'Your screenshot couldn’t upload, so your feedback was sent without it.',
} as const;

const SENDING_TOAST = { tone: 'info', title: 'Sending the screenshot…', sticky: true } as const;

// APP-104 §3.2: what the review says about the recording before it.
const UNCONFIRMED =
  'We couldn’t confirm every word was transcribed. Check the text before you send.';
const SLOW = 'The connection is slow, so we stopped recording. Check the text before you send.';
// Added in the build (§3.2): the day's voice time, a session that never started, another one
// still finishing, and why a session ended by itself.
const VOICE_USED = 'You’ve used today’s voice time. Typing works as usual.';
const NOT_STARTED =
  'Voice couldn’t start, so nothing you said was transcribed. Type it here instead.';
const TOO_MANY = 'Another recording is still finishing. Try again in a moment.';
const ENDED_BY_ITSELF: Partial<Record<VoiceEndReason, string>> = {
  silence: 'Recording stopped after 15 seconds of quiet.',
  no_audio: 'Recording stopped because no sound came through for 15 seconds.',
  recording_cap: 'Recording stops at 5 minutes.',
  daily_limit: 'That used the rest of today’s voice time.',
};

/** A ticket from the draft's open is used for the first recording if it is this fresh. */
const TICKET_REUSE_MS = 100_000;

/**
 * After the browser asks "leave this page?", this long to see whether the page really goes
 * (pagehide). If it is still there, the person stayed, and a cut connection is a real failure.
 */
const LEAVING_MS = 1_000;

type Upload = 'idle' | 'running' | 'done' | 'failed';

/** One opening of the dialog: everything the click captured and the work still going. */
interface Session {
  id: string;
  page: FeedbackPage;
  client: ReturnType<typeof clientState>;
  diagnostics: ReturnType<typeof snapshot>;
  cancelled: boolean;
  captured: Promise<CapturedScreen>;
  capture: CapturedScreen | null;
  /** This opening's own object URL for the preview; revoked only by this opening. */
  url: string | null;
  /** The draft exists on the server. */
  opened: boolean;
  /** The upload's end was already told to the person. */
  told: boolean;
  draft: Promise<void> | null;
  upload: Upload;
  uploadAbort: AbortController | null;
  /** Called with the upload's end once the report was sent while it was still going. */
  afterSend: ((outcome: 'attached' | 'failed') => void) | null;
  /** The toast and the leave prompt, from Send until the upload ends (QA-058). */
  guard: (() => void) | null;
  /**
   * When the browser last asked whether to leave (beforeunload); null again once the page is back
   * or seen again, or is still here a second on (they stayed).
   */
  leftAt: number | null;
  /** The page was hidden for good (pagehide). */
  pageHidden: boolean;
  /** The upload was cut by our own 240 s limit. */
  timedOut: boolean;
  /** The voice ticket the draft's open gave (one use, 2 minutes), and when. */
  voice: { ticket: string | null; at: number; used: boolean } | null;
  /** An open gave no ticket because today's voice time has run out (from `/v1/me`). */
  usedUp: boolean;
}

function openBody(s: Session, cap: CapturedScreen): OpenFeedback {
  return { id: s.id, page: s.page, client: s.client, screenshot: cap.declared };
}

function noShotReason(cap: CapturedScreen): string {
  const reason = cap.declared.state === 'unavailable' ? cap.declared.reason : 'failed';
  return NO_SHOT[reason] ?? 'it couldn’t be taken.';
}

const nextPaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });

function pageNow(area: string, hero: 'full' | 'slim'): FeedbackPage {
  // Only a path the report's contract accepts; anything odd is recorded as the site root.
  const path = recordable(window.location.pathname) ?? '/';
  const last = snapshot().pages.at(-1);
  const tab = document.querySelector('[role="tab"][aria-selected="true"]')?.textContent.trim();
  return {
    path,
    routeId: last?.path === path ? last.routeId : null,
    title: document.title.slice(0, 200),
    area: area.slice(0, 100),
    tab: tab ? tab.slice(0, 100) : null,
    hero,
  };
}

/**
 * From Send until the upload ends the browser asks before the page is left, and the page notes
 * that it is going. Returns what takes both away.
 */
function guardLeaving(s: Session): () => void {
  let stayCheck: number | undefined;
  const ask = (event: BeforeUnloadEvent): void => {
    s.leftAt = Date.now();
    // Still here a second on (they chose to stay): a later cut is a real failure, even with the
    // tab hidden by then.
    window.clearTimeout(stayCheck);
    stayCheck = window.setTimeout(() => {
      if (!s.pageHidden) s.leftAt = null;
    }, LEAVING_MS);
    event.preventDefault();
    // Older Chromium only asks when this is set as well.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    event.returnValue = '';
  };
  const gone = (): void => {
    s.pageHidden = true;
  };
  const back = (): void => {
    s.leftAt = null;
    s.pageHidden = false;
  };
  // Seen again: whatever leave was asked about didn't happen.
  const seen = (): void => {
    if (document.visibilityState === 'visible') s.leftAt = null;
  };
  window.addEventListener('beforeunload', ask);
  window.addEventListener('pagehide', gone);
  window.addEventListener('pageshow', back);
  document.addEventListener('visibilitychange', seen);
  return () => {
    window.clearTimeout(stayCheck);
    window.removeEventListener('beforeunload', ask);
    window.removeEventListener('pagehide', gone);
    window.removeEventListener('pageshow', back);
    document.removeEventListener('visibilitychange', seen);
  };
}

/** The shapes a cut connection takes: the app's own "no answer" error, or an aborted request. */
function connectionCut(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 0 || error.code === 'network_error';
  return (
    error instanceof DOMException && (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

/**
 * Was the connection cut because the page is going away? Yes once the page was hidden (pagehide)
 * or after beforeunload, if pagehide follows within a second. A leave the person cancelled leaves
 * the page visible, so that is a real failure.
 */
async function isLeaving(s: Session): Promise<boolean> {
  // Read through a function: pagehide can arrive while this waits.
  const hidden = (): boolean => s.pageHidden;
  if (hidden()) return true;
  if (s.leftAt === null) return false;
  const wait = LEAVING_MS - (Date.now() - s.leftAt);
  if (wait > 0)
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, wait);
    });
  if (hidden() || document.visibilityState === 'hidden') return true;
  s.leftAt = null;
  return false;
}

/** Says how the upload ended after the report went, once. */
function tell(s: Session, outcome: 'attached' | 'failed'): void {
  if (s.afterSend === null || s.told) return;
  s.told = true;
  s.afterSend(outcome);
}

export function FeedbackFlow({
  features,
  area,
  hero,
  startTyping = false,
  onClose,
  onOff,
  onSessionEnded,
  onVoiceUsed,
  onSent,
}: FeedbackFlowProps): ReactElement {
  // The provider's API is stable, so an upload that outlives the dialog can still toast.
  const toast = useToast();
  const session = useRef<Session | null>(null);
  const [words, setWords] = useState('');
  const [wordsError, setWordsError] = useState<string | null>(null);
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [include, setInclude] = useState(true);
  const [shot, setShot] = useState<ShotView>({ state: 'taking' });
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const includeRef = useRef(true);
  useEffect(() => {
    includeRef.current = include;
  }, [include]);

  // Voice (§3.2). Offered with time left today: the click records first. Used up: the review, with
  // a calm line, and the dictation tip may show.
  const usedUp = features.voice && features.voiceSecondsLeft === 0;
  const recordFirst = features.voice && !usedUp && !startTyping;
  const [mode, setMode] = useState<'voice' | 'review'>(recordFirst ? 'voice' : 'review');
  /** Voice can't be offered again in this opening (the day's time, or the server said no). */
  const [voiceOff, setVoiceOff] = useState(!features.voice || usedUp);
  const [voicePhase, setVoicePhase] = useState<RecorderPhase>('starting');
  const [since, setSince] = useState<number | null>(null);
  const [lastPhrase, setLastPhrase] = useState('');
  const [phraseCount, setPhraseCount] = useState(0);
  const [partial, setPartial] = useState('');
  const [capSeconds, setCapSeconds] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(usedUp ? VOICE_USED : null);
  const [mic, setMic] = useState<(MicrophoneHelp & { allowed?: boolean }) | null>(null);
  // The loudness, written as frames arrive and read by the bar once a frame (not React state).
  const level = useRef(0);
  /** Every recording this opening started; the last may still be running. */
  const recorders = useRef<Recorder[]>([]);
  /** The words that came from voice, as they went into the box (for Send's `input`). */
  const heard = useRef('');
  const wordsRef = useRef('');
  useEffect(() => {
    wordsRef.current = words;
  }, [words]);

  const startUpload = useCallback((s: Session): void => {
    const cap = s.capture;
    if (cap?.state !== 'captured' || s.upload !== 'idle' || s.cancelled) return;
    s.upload = 'running';
    const abort = new AbortController();
    s.uploadAbort = abort;
    const timer = window.setTimeout(() => {
      s.timedOut = true;
      abort.abort();
    }, UPLOAD_TIMEOUT_MS);
    void (async () => {
      try {
        await uploadScreenshot(s.id, cap.bytes, abort.signal);
        s.upload = 'done';
        tell(s, 'attached');
      } catch (error) {
        if (s.cancelled) return;
        s.upload = 'failed';
        // QA-058: cut because the page is going away. The bytes may still reach the server and
        // attach; if not, the attach window settles the report. Saying "failed" now would refuse them.
        if (!s.timedOut && connectionCut(error) && (await isLeaving(s))) {
          s.guard?.();
          return;
        }
        // A refused file (any 4xx) says so; a timeout or a dropped connection is a failed upload.
        const refused = error instanceof ApiError && error.status >= 400 && error.status < 500;
        await completeScreenshot(s.id, {
          omitted: refused ? 'upload_refused' : 'upload_failed',
          ...(error instanceof ApiError && { status: error.status }),
        }).catch(() => undefined);
        tell(s, 'failed');
      } finally {
        window.clearTimeout(timer);
      }
    })();
  }, []);

  /** Opens the draft once the picture is ready; a failed open is tried again at Send. */
  const ensureDraft = useCallback(
    (s: Session): Promise<void> => {
      s.draft ??= (async () => {
        const cap = await s.captured;
        s.capture = cap;
        try {
          const opened = await openFeedback(openBody(s, cap));
          s.voice = { ticket: opened.voice.ticket, at: Date.now(), used: false };
        } catch (error) {
          s.draft = null;
          throw error;
        }
        s.opened = true;
        if (includeRef.current) startUpload(s);
      })();
      return s.draft;
    },
    [startUpload],
  );

  /**
   * Stops any recording, and closes the socket of one that ended with Type instead and is still
   * saving. Not at Send: that socket closes itself once the server has saved the words.
   */
  const stopRecorders = useCallback((): void => {
    for (const r of recorders.current) r.cancel();
    recorders.current = [];
  }, []);

  /** Ends an opening: frees the picture and, unless the report is waiting on it, the upload. */
  const finish = useCallback((s: Session, keepUpload: boolean): void => {
    if (!keepUpload) {
      s.cancelled = true;
      s.uploadAbort?.abort();
    }
    if (s.url !== null) URL.revokeObjectURL(s.url);
    s.url = null;
  }, []);

  /** Closes the flow for good. `discardDraft` throws away a draft that exists. */
  const endFlow = useCallback(
    (s: Session, discardDraft: boolean): void => {
      stopRecorders();
      finish(s, false);
      if (discardDraft && s.draft !== null)
        void s.draft.then(() => discardFeedback(s.id)).catch(() => undefined);
      session.current = null;
      onClose();
    },
    [finish, onClose, stopRecorders],
  );

  /**
   * What a failed call means. Feedback switched off and an ended session close the flow (the app
   * is told); anything else is fixed words, or nothing when `quiet`.
   */
  const handleFailure = useCallback(
    (s: Session, error: unknown, phase: 'open' | 'send', quiet: boolean): void => {
      if (error instanceof ApiError && error.code === 'feedback_off') {
        endFlow(s, true);
        onOff();
        return;
      }
      if (isNotSignedIn(error)) {
        endFlow(s, false);
        onSessionEnded();
        return;
      }
      if (quiet) return;
      if (error instanceof ApiError && error.code === 'words_required') {
        setWordsError(WORDS_REQUIRED);
        return;
      }
      if (error instanceof ApiError && error.code === 'feedback_limit')
        setProblem(phase === 'open' ? OPEN_LIMIT_WORDS : LIMIT_WORDS);
      else setProblem(TRY_AGAIN);
    },
    [endFlow, onOff, onSessionEnded],
  );

  /**
   * The ticket for a recording: the one the draft's open gave, if it's unused and fresh; otherwise
   * (a retry, or Record later) the draft is opened again for a new one. A repeat open replaces the
   * earlier ticket, so the first recording never opens again.
   */
  const takeTicket = useCallback(
    async (s: Session, renew: boolean): Promise<VoiceTicket | null> => {
      await ensureDraft(s);
      // No ticket: was it today's time running out (a calm line), or voice being off now?
      const none = async (): Promise<null> => {
        try {
          s.usedUp = await voiceUsedUp();
        } catch {
          s.usedUp = false;
        }
        return null;
      };
      const offered = s.voice;
      if (!renew && offered !== null && !offered.used) {
        offered.used = true;
        if (offered.ticket === null) return none();
        if (Date.now() - offered.at < TICKET_REUSE_MS)
          return { reportId: s.id, ticket: offered.ticket };
      }
      const cap = s.capture;
      if (cap === null) return null;
      const opened = await openFeedback(openBody(s, cap));
      s.voice = { ticket: opened.voice.ticket, at: Date.now(), used: true };
      return opened.voice.ticket === null
        ? none()
        : { reportId: s.id, ticket: opened.voice.ticket };
    },
    [ensureDraft],
  );

  /** A recording ended: its words go into the box, and the review says what it needs to. */
  const toReview = useCallback(
    async (s: Session, result: RecorderResult): Promise<void> => {
      onVoiceUsed?.();
      const said = wordsOf(result);
      if (said !== '') {
        setWords((prev) => (prev.trim() === '' ? said : `${prev.trimEnd()}\n${said}`));
        heard.current = heard.current === '' ? said : `${heard.current}\n${said}`;
      }
      const end = result.end;
      let next: string | null = null;
      let help: MicrophoneHelp | null = null;
      switch (end.kind) {
        case 'saved':
          // A session that ended by itself says why, calmly.
          next = ENDED_BY_ITSELF[end.reason] ?? null;
          if (end.reason === 'daily_limit') setVoiceOff(true);
          break;
        case 'typed':
          break;
        case 'unconfirmed':
          next = UNCONFIRMED;
          break;
        case 'slow':
          next = SLOW;
          break;
        case 'not_started':
          if (end.code === 'daily_limit' || (end.code === 'unavailable' && s.usedUp)) {
            next = VOICE_USED;
            setVoiceOff(true);
          } else if (end.code === 'too_many_sessions') {
            next = TOO_MANY;
          } else {
            next = NOT_STARTED;
            // Voice isn't offered now (a switch, the audience, a discarded or sent draft).
            if (['unavailable', 'ticket_invalid', 'report_sent'].includes(end.code))
              setVoiceOff(true);
          }
          break;
        case 'microphone':
          if (isMicrophoneError(end.error)) help = await explainMicrophone(end.error);
          else {
            // This browser can't give a microphone to the page at all.
            next = NOT_STARTED;
            setVoiceOff(true);
          }
          break;
      }
      if (s.cancelled) return;
      setNotice(next);
      setMic(help);
      setPartial('');
      setMode('review');
    },
    [onVoiceUsed],
  );

  /** Starts the microphone and the recording; the bar shows its progress. */
  const launch = useCallback(
    (s: Session): void => {
      level.current = 0;
      const left = features.voiceSecondsLeft;
      const r = startRecorder({
        openMicrophone,
        ticket: (renew) => takeTicket(s, renew),
        connect: () => new WebSocket(voiceUrl()),
        ...(left !== undefined && { capMs: left * 1_000 }),
        events: {
          phase: (p) => {
            if (s.cancelled) return;
            setVoicePhase(p);
            if (p === 'recording') setSince(Date.now());
          },
          level: (rms) => {
            level.current = Math.max(level.current, Math.min(1, rms * 6));
          },
          partial: setPartial,
          phrase: (p) => {
            setLastPhrase(p.text);
            setPhraseCount((n) => n + 1);
            setPartial('');
          },
          limit: (maxMs) => {
            // Under the 5-minute cap only when the day's time is nearly gone: then the session's
            // own cap is the time left. A normal session says nothing about the day.
            if (maxMs < FEEDBACK_VOICE_LIMITS.recordingMs) setCapSeconds(Math.floor(maxMs / 1_000));
          },
          end: (result) => {
            if (!s.cancelled) void toReview(s, result);
          },
        },
      });
      recorders.current.push(r);
    },
    [features.voiceSecondsLeft, level, takeTicket, toReview],
  );

  /** Back to the bar for another recording (Record, Try again): a fresh bar, then the microphone. */
  const startVoice = useCallback(
    (s: Session): void => {
      setMode('voice');
      setVoicePhase('starting');
      setSince(null);
      setLastPhrase('');
      setPartial('');
      setCapSeconds(null);
      setNotice(null);
      setMic(null);
      launch(s);
    },
    [launch],
  );

  const current = (): Recorder | undefined => recorders.current.at(-1);

  /** Record (or Try again): back to the bar; what they say is added to their words. */
  const record = useCallback((): void => {
    const s = session.current;
    if (s !== null) startVoice(s);
  }, [startVoice]);

  // A blocked microphone: once the browser allows it, recording starts by itself, unless words
  // are already in the box; then Record adds to them (§3.2).
  useEffect(() => {
    if (mic === null || mic.allowed === true || mode !== 'review') return;
    return watchMicrophone(mic, () => {
      if (wordsRef.current.trim() !== '')
        setMic((m) =>
          m === null
            ? m
            : {
                ...m,
                allowed: true,
                title: 'Microphone allowed',
                step: 'Select Record to add to your feedback by voice.',
              },
        );
      else record();
    });
  }, [mic, mode, record]);

  // Each opening takes the diagnostics and the page at the click, then starts the picture and the
  // draft while the dialog is already on screen.
  useEffect(() => {
    // The menu and the Help panel have closed; wait for the page to paint without them.
    const captured = nextPaint().then(captureScreen);
    const s: Session = {
      id: crypto.randomUUID(),
      page: pageNow(area, hero),
      client: clientState(),
      diagnostics: snapshot(),
      cancelled: false,
      captured,
      capture: null,
      url: null,
      opened: false,
      told: false,
      draft: null,
      upload: 'idle',
      uploadAbort: null,
      afterSend: null,
      guard: null,
      leftAt: null,
      pageHidden: false,
      timedOut: false,
      voice: null,
      usedUp: false,
    };
    session.current = s;
    // The microphone starts at the click (the first render already shows the bar), and the bar
    // is on screen before the page is drawn.
    if (recordFirst) launch(s);
    void captured.then((cap) => {
      if (s.cancelled) return;
      // The result is shared by everyone who asked while it was drawing, so each opening makes
      // (and frees) its own object URL.
      if (cap.state === 'captured') {
        s.url = URL.createObjectURL(
          new Blob([cap.bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' }),
        );
        setShot({ state: 'ready', url: s.url });
      } else setShot({ state: 'none', reason: noShotReason(cap) });
      // A failed open says nothing now; Send tries it again.
      void ensureDraft(s).catch((error: unknown) => {
        if (!s.cancelled) handleFailure(s, error, 'open', true);
      });
    });
    return () => {
      // Closed without sending (or a development remount): this opening ends here. A sent one
      // clears `session` first, so its upload carries on.
      if (session.current === s) {
        stopRecorders();
        finish(s, false);
        session.current = null;
      }
    };
    // One opening per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const discard = useCallback((): void => {
    const s = session.current;
    // A draft that was never opened has nothing to throw away; one being opened is thrown away
    // once it exists.
    if (s !== null) endFlow(s, true);
    else onClose();
  }, [endFlow, onClose]);

  /**
   * The report went while its screenshot was still uploading: say so and ask before the page is
   * left until the upload ends (QA-058), then say how it ended.
   */
  const followUpload = (s: Session): void => {
    if (s.upload === 'running') {
      const stop = guardLeaving(s);
      const shown = toast.show(SENDING_TOAST);
      s.guard = () => {
        stop();
        toast.dismiss(shown);
        s.guard = null;
      };
    }
    s.afterSend = (outcome) => {
      s.guard?.();
      toast.show(
        outcome === 'attached'
          ? { tone: 'done', title: 'Your screenshot is attached.' }
          : NO_UPLOAD_TOAST,
      );
    };
    // It may have ended while the report was on its way.
    if (s.upload === 'done') tell(s, 'attached');
    if (s.upload === 'failed') tell(s, 'failed');
  };

  const send = (): void => {
    const s = session.current;
    if (s === null || sending) return;
    if (words.trim() === '') {
      setWordsError(WORDS_REQUIRED);
      return;
    }
    setWordsError(null);
    setProblem(null);
    setSending(true);
    void (async () => {
      try {
        await ensureDraft(s);
      } catch (error) {
        setSending(false);
        handleFailure(s, error, 'open', false);
        return;
      }
      // Send never waits for the upload: a running one is sent as still attaching.
      let attaching = false;
      try {
        const cap = s.capture;
        const choice = !include ? 'exclude' : cap?.state === 'captured' ? 'include' : 'unavailable';
        if (choice === 'include') startUpload(s);
        attaching = choice === 'include' && s.upload === 'running';
        const spoken = heard.current.trim();
        const sent = await submitFeedback(s.id, {
          words,
          ...(kind !== null && { kind }),
          input: spoken === '' ? 'typed' : words.trim() === spoken ? 'spoken' : 'both',
          screenshot: choice,
          screenshotAttaching: attaching,
          diagnostics: s.diagnostics,
        });
        toast.show({
          tone: 'done',
          title: `Thanks. We got your feedback (${sent.displayId}).`,
          body: FOLLOW_IT,
        });
        onSent?.();
        // The upload is still going: say so, and ask before the page is left, until it ends.
        if (attaching) followUpload(s);
        else if (choice === 'include' && s.upload === 'failed') {
          // It failed before Send: the person ticked the screenshot, so they hear it didn't go.
          toast.show(NO_UPLOAD_TOAST);
        }
        finish(s, attaching);
        session.current = null;
        onClose();
      } catch (error) {
        if (error instanceof ApiError && error.code === 'feedback_already_sent') {
          // The answer was lost and this is the retry: it was sent, there's no number to give.
          // It closes as a Send does: a socket still saving the words is left to finish. A
          // screenshot still uploading goes on, and the server attaches it within its window or
          // doesn't; the upload's own toasts say which.
          if (attaching) {
            toast.show({ tone: 'done', title: 'This feedback was already sent.' });
            followUpload(s);
          } else
            toast.show({ tone: 'done', title: 'Thanks. We got your feedback.', body: FOLLOW_IT });
          onSent?.();
          finish(s, attaching);
          session.current = null;
          onClose();
          return;
        }
        setSending(false);
        handleFailure(s, error, 'send', false);
      }
    })();
  };

  if (mode === 'voice')
    return (
      <RecordingBar
        phase={voicePhase}
        since={since}
        level={level}
        lastPhrase={lastPhrase}
        phraseCount={phraseCount}
        partial={partial}
        secondsLeft={
          capSeconds !== null && capSeconds < (features.voiceSecondsLeft ?? Infinity)
            ? capSeconds
            : (features.voiceSecondsLeft ?? null)
        }
        onDone={() => {
          current()?.done();
        }}
        onTypeInstead={() => {
          current()?.typeInstead();
        }}
        onCancel={discard}
      />
    );

  // Offered for Record while the day's time lasts (`/me` is read again after each recording).
  const offerVoice = features.voice && !voiceOff && features.voiceSecondsLeft !== 0;
  return (
    <FeedbackReview
      isOpen
      words={words}
      onWords={(next) => {
        setWords(next);
        if (next.trim() !== '') setWordsError(null);
      }}
      wordsError={wordsError}
      kind={kind}
      onKind={setKind}
      include={include}
      onInclude={setInclude}
      shot={shot}
      dictation={features.dictationTip && !offerVoice ? dictationPlatform() : null}
      notice={notice}
      micHelp={
        mic === null
          ? null
          : {
              help: mic,
              retryLabel: mic.allowed === true || words.trim() !== '' ? 'Record' : 'Try again',
              onRetry: record,
            }
      }
      onRecord={offerVoice && mic === null ? record : null}
      sending={sending}
      problem={problem}
      onSend={send}
      onDiscard={discard}
    />
  );
}
